import { useEffect, useState } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import { AuthLayout } from './components/layout/AuthLayout';
import { AppLayout } from "./components/layout/AppLayout";
import { SignInPage } from './pages/SignInPage';
import { OnboardingPage } from "./pages/OnboardingPage";
import { ChatPage } from "./pages/ChatPage";
import { MonitorPage } from "./pages/MonitorPage";
import { ChangesPage } from './pages/ChangesPage';
import { DocumentPage } from './pages/DocumentPage';
import { PlanPage } from './pages/PlanPage';
import { ReportPage } from './pages/ReportPage';
import { LicenseReportPage } from './pages/LicenseReportPage';
import { AuditLogPage } from './pages/AuditLogPage';
import { SettingsPage } from './pages/SettingsPage';
import { AccountPage } from './pages/AccountPage';
import { PricingPage } from './pages/PricingPage';
import { SearchPage } from './pages/SearchPage';
import { AuthCallbackPage } from "./pages/AuthCallbackPage";
import { OAuthPopupCallback } from "./pages/OAuthPopupCallback";
import { ProtectedRoute } from "./components/ProtectedRoute";
import { ProGate } from "./components/billing/ProGate";
import { useAuthStore } from "./stores/authStore";
import { useBillingStore } from "./stores/billingStore";
import { NotFoundPage } from "./pages/NotFoundPage";
import { Toaster } from "./components/ui/Toaster";

export default function App() {
  const checkSession = useAuthStore((s) => s.checkSession);
  const token = useAuthStore((s) => s.token);
  const refreshAccessToken = useAuthStore((s) => s.refreshAccessToken);
  const fetchBillingStatus = useBillingStore((s) => s.fetchStatus);
  const [bootstrapped, setBootstrapped] = useState(!!token);

  useEffect(() => {
    if (token) {
      setBootstrapped(true);
      checkSession();
      fetchBillingStatus();
    } else {
      // No token in memory — try silent refresh from cookie
      refreshAccessToken().then((success) => {
        setBootstrapped(true);
        if (success) {
          fetchBillingStatus();
        }
      });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (!bootstrapped) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background">
        <div className="animate-pulse text-muted-foreground">Loading...</div>
      </div>
    );
  }

  return (
    <>
    <Routes>
      {/* Sign-in page with minimal header */}
      <Route element={<AuthLayout />}>
        <Route path="/" element={<SignInPage />} />
      </Route>

      {/* Auth callback (handles ?token= from OAuth/OTP) */}
      <Route path="/auth/callback" element={<AuthCallbackPage />} />

      {/* OAuth popup callback (used by re-auth flow in chat) */}
      <Route path="/oauth/popup-callback" element={<OAuthPopupCallback />} />

      {/* Onboarding (standalone, no layout) */}
      <Route path="/onboarding" element={<OnboardingPage />} />

      {/* Protected routes with app layout (sidebar navigation) */}
      <Route element={<ProtectedRoute><AppLayout /></ProtectedRoute>}>
        <Route path="/chat" element={<ChatPage />} />
        <Route path="/chat/:id" element={<ChatPage />} />
        <Route path="/search" element={<SearchPage />} />
        <Route path="/monitor" element={<ProGate feature="accountAnswers"><MonitorPage /></ProGate>} />
        <Route path="/browser" element={<Navigate to="/chat" replace />} />
        <Route path="/changes" element={<ProGate feature="nlConfig"><ChangesPage /></ProGate>} />
        <Route path="/changes/documents/:id" element={<ProGate feature="nlConfig"><DocumentPage /></ProGate>} />
        <Route path="/changes/:id" element={<ProGate feature="nlConfig"><PlanPage /></ProGate>} />
        <Route path="/report" element={<ReportPage />} />
        <Route path="/license" element={<ProGate feature="healthCheck"><LicenseReportPage /></ProGate>} />
        <Route path="/audit" element={<ProGate feature="auditTrail"><AuditLogPage /></ProGate>} />
        <Route path="/settings" element={<SettingsPage />} />
        <Route path="/account" element={<AccountPage />} />
        <Route path="/pricing" element={<PricingPage />} />
      </Route>

      {/* 404 */}
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
    <Toaster />
  </>
  );
}
